import React, { useEffect, useState } from "react";
import { sendFeedback } from "../../feedback/sendFeedback";
import { getTesterProfile } from "../../helpers/testerAccess";

// Total across all user attachments — the Gmail send endpoint caps the whole
// message (body + graph snapshot + uploads) at ~5 MB JSON / ~3.5 MB raw.
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // 5 MB

const FeedbackModal = ({
  open,
  onClose,
  screenLabel,
  inputs,
  attachGraph = true,
  redirectUrl,
  // Run just before the user actually leaves. Cancelling or dismissing the
  // modal does not call it — they are still using the app.
  onBeforeLeave,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    const p = getTesterProfile();
    if (p) {
      setName(p.name || "");
      setEmail(p.email || "");
    }
    setDescription("");
    setAttachments([]);
    setRating(0);
    setHoverRating(0);
    setStatus("idle");
    setErrorMsg("");
  }, [open]);

  const closeOrRedirect = async () => {
    if (onBeforeLeave) {
      // Never let a failed sign-out strand the user in the app they asked to
      // leave; the redirect happens either way.
      try {
        await onBeforeLeave();
      } catch (e) {
        // ignore
      }
    }
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && status !== "sending") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, status, onClose]);

  const handleFile = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-picking the same file after a remove
    if (!picked.length) return;
    const next = [...attachments];
    picked.forEach((file) => {
      const isDuplicate = next.some(
        (f) => f.name === file.name && f.size === file.size
      );
      if (!isDuplicate) next.push(file);
    });
    const totalBytes = next.reduce((sum, f) => sum + f.size, 0);
    if (totalBytes > MAX_ATTACHMENT_BYTES) {
      setErrorMsg("Attachments must total 5 MB or smaller.");
      return;
    }
    setErrorMsg("");
    setAttachments(next);
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !description.trim()) {
      setErrorMsg("Please fill in name, email, and a description.");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      await sendFeedback({
        name: name.trim(),
        email: email.trim(),
        description: description.trim(),
        screenLabel,
        inputs,
        attachments,
        attachGraph,
        rating,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err && err.message ? err.message : "Failed to send feedback.");
    }
  };

  if (!open) return null;

  const sending = status === "sending";
  const success = status === "success";

  return (
    <>
      <div
        role="presentation"
        onClick={sending ? undefined : onClose}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 9999,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(94vw, 520px)",
          maxHeight: "92vh",
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          zIndex: 10000,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            backgroundColor: "#a4243b",
            color: "#fff",
          }}
        >
          <span id="feedback-title" style={{ fontSize: "16px", fontWeight: 600 }}>
            Send Feedback
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: "22px",
              cursor: sending ? "not-allowed" : "pointer",
              lineHeight: 1,
              padding: "0 4px",
              opacity: sending ? 0.5 : 1,
            }}
            aria-label="Close feedback"
          >
            &times;
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            padding: "16px 18px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {success ? (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: "#1a7f37",
                fontSize: "14px",
              }}
            >
              <div style={{ fontSize: "32px", marginBottom: "8px" }}>✓</div>
              Thanks — your feedback has been sent.
              <div style={{ marginTop: "16px" }}>
                <button
                  type="button"
                  onClick={closeOrRedirect}
                  className="btn btn-m rounded-l bg-green-dark font-900 text-uppercase"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "-4px" }}>
                Screen: <strong>{screenLabel || "—"}</strong>
                {attachGraph ? (
                  <>
                    <br />A snapshot of the current graph and its inputs is attached
                    automatically.
                  </>
                ) : null}
              </div>

              <label style={{ fontSize: "12px", color: "#444" }}>Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={sending}
                style={inputStyle}
              />

              <label style={{ fontSize: "12px", color: "#444" }}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={sending}
                style={inputStyle}
              />

              <label style={{ fontSize: "12px", color: "#444" }}>
                Rating (optional)
              </label>
              <div style={{ fontSize: "26px", lineHeight: 1, userSelect: "none" }}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const filled = (hoverRating || rating) >= n;
                  return (
                    <span
                      key={n}
                      role="button"
                      aria-label={`${n} star${n > 1 ? "s" : ""}`}
                      onClick={() => !sending && setRating(n === rating ? 0 : n)}
                      onMouseEnter={() => !sending && setHoverRating(n)}
                      onMouseLeave={() => !sending && setHoverRating(0)}
                      style={{
                        cursor: sending ? "not-allowed" : "pointer",
                        color: filled ? "#f5b301" : "#ddd",
                        marginRight: "2px",
                        transition: "color 0.1s ease",
                      }}
                    >
                      ★
                    </span>
                  );
                })}
                {rating > 0 && (
                  <span style={{ fontSize: "12px", color: "#666", marginLeft: "8px" }}>
                    {rating} / 5
                  </span>
                )}
              </div>

              <label style={{ fontSize: "12px", color: "#444" }}>
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                disabled={sending}
                placeholder="What went wrong, what would you change, what was unexpected…"
                style={{ ...inputStyle, resize: "vertical", minHeight: "100px" }}
              />

              <label style={{ fontSize: "12px", color: "#444" }}>
                Attachments (optional, max 5&nbsp;MB total)
              </label>
              <input
                type="file"
                multiple
                onChange={handleFile}
                disabled={sending}
                style={{ fontSize: "13px" }}
              />
              {attachments.length > 0 && (
                <div style={{ fontSize: "11px", color: "#666" }}>
                  {attachments.map((file, idx) => (
                    <div
                      key={`${file.name}-${file.size}`}
                      style={{ display: "flex", alignItems: "center", gap: "6px" }}
                    >
                      <span>
                        {file.name} ({Math.round(file.size / 1024)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx)}
                        disabled={sending}
                        aria-label={`Remove ${file.name}`}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#dc3545",
                          cursor: sending ? "not-allowed" : "pointer",
                          fontSize: "14px",
                          lineHeight: 1,
                          padding: "0 2px",
                        }}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {errorMsg && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errorMsg}
                </div>
              )}

              <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                <button
                  type="submit"
                  disabled={sending}
                  className="btn btn-m rounded-l bg-green-dark font-900 text-uppercase"
                  style={{ flex: 1 }}
                >
                  {sending ? "Sending…" : "Send"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={sending}
                  className="btn btn-m rounded-l font-900 text-uppercase"
                  style={{ flex: 1, background: "#eee", color: "#333" }}
                >
                  Cancel
                </button>
                {redirectUrl && (
                  <button
                    type="button"
                    onClick={closeOrRedirect}
                    disabled={sending}
                    className="btn btn-m rounded-l font-900 text-uppercase"
                    style={{ flex: 1, background: "#eee", color: "#333" }}
                  >
                    Skip
                  </button>
                )}
              </div>
            </>
          )}
        </form>
      </div>
    </>
  );
};

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  fontSize: "14px",
  border: "1px solid #ced4da",
  borderRadius: "6px",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

export default FeedbackModal;
